import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { PdfReportButton } from "@/components/PdfReportButton";
import { ArrowLeft, CalendarX, Filter } from "lucide-react";

const monthLabel = (ym) => {
    if (!ym) return "Të gjithë muajt";
    const [y, m] = ym.split("-");
    const months = ["Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor", "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor"];
    return `${months[parseInt(m) - 1]} ${y}`;
};

export default function ParentAbsences() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const currentYM = new Date().toISOString().slice(0, 7);
    const [monthFilter, setMonthFilter] = useState(currentYM);
    const [subjectFilter, setSubjectFilter] = useState("all");
    const [reasonFilter, setReasonFilter] = useState("all");

    useEffect(() => {
        api.get(`/students/${id}/absences`).then((r) => setData(r.data)).finally(() => setLoading(false));
    }, [id]);

    const { months, subjects, filtered, filteredHours } = useMemo(() => {
        if (!data) return { months: [], subjects: [], filtered: [], filteredHours: 0 };
        const months = Array.from(new Set(data.absences.map((a) => a.date?.slice(0, 7)).filter(Boolean))).sort().reverse();
        const subjects = Array.from(new Set(data.absences.map((a) => a.subject_name).filter(Boolean))).sort();
        const filtered = data.absences.filter((a) => {
            if (monthFilter !== "all" && a.date?.slice(0, 7) !== monthFilter) return false;
            if (subjectFilter !== "all" && a.subject_name !== subjectFilter) return false;
            if (reasonFilter !== "all" && (a.reason || "unexcused") !== reasonFilter) return false;
            return true;
        });
        const filteredHours = filtered.reduce((s, a) => s + (a.hours || 1), 0);
        return { months, subjects, filtered, filteredHours };
    }, [data, monthFilter, subjectFilter, reasonFilter]);

    return (
        <Layout>
            <Link to="/parent" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4" data-testid="back-to-overview">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>

            {loading ? <div className="text-slate-500">Duke ngarkuar…</div> : !data ? <div>Nuk u gjet.</div> : (
                <>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Mungesat</div>
                            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">{data.student.name}</h1>
                            <div className="mt-3 flex gap-6">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Total orë</div>
                                    <div className="font-heading text-3xl font-black text-terracotta" data-testid="absence-total-hours">{data.total_hours}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Total mungesa</div>
                                    <div className="font-heading text-3xl font-black text-oxford" data-testid="absence-total-count">{data.total_count}</div>
                                </div>
                            </div>
                        </div>
                        <PdfReportButton studentId={id} />
                    </div>

                    {/* Filters */}
                    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="absences-filters">
                        <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500 font-bold shrink-0">
                            <Filter className="w-4 h-4" /> Filtro
                        </div>
                        <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white text-sm px-2 font-semibold flex-1 sm:flex-none" data-testid="absences-filter-month">
                            <option value="all">Të gjithë muajt</option>
                            {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                        </select>
                        <select value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white text-sm px-2 font-semibold flex-1 sm:flex-none" data-testid="absences-filter-subject">
                            <option value="all">Të gjitha lëndët</option>
                            {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white text-sm px-2 font-semibold flex-1 sm:flex-none" data-testid="absences-filter-reason">
                            <option value="all">Të gjitha arsyet</option>
                            <option value="excused">Me arsye</option>
                            <option value="unexcused">Pa arsye</option>
                        </select>
                        <div className="ml-auto text-xs text-slate-500" data-testid="absences-result-count">
                            <span className="font-bold text-oxford">{filtered.length}</span> mungesa • <span className="font-bold text-terracotta">{filteredHours}</span> orë
                        </div>
                    </div>

                    {filtered.length === 0 ? (
                        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="parent-no-absences">
                            <CalendarX className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                            <div className="font-heading text-xl font-bold text-slate-800">Pa mungesa për këtë filtër</div>
                            <p className="text-sm text-slate-600 mt-1">Provoni një muaj ose lëndë tjetër.</p>
                        </div>
                    ) : (
                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-bone/60 text-xs uppercase tracking-widest text-slate-600">
                                    <tr>
                                        <th className="text-left px-4 sm:px-6 py-3">Data</th>
                                        <th className="text-left px-4 sm:px-6 py-3">Lënda</th>
                                        <th className="text-left px-2 sm:px-4 py-3">Orë</th>
                                        <th className="text-left px-2 sm:px-4 py-3">Arsyeja</th>
                                        <th className="text-left px-4 sm:px-6 py-3 hidden sm:table-cell">Komenti</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {filtered.map((a) => {
                                        const reason = a.reason || "unexcused";
                                        const reasonChip = reason === "excused"
                                            ? <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-sage/10 text-sage text-xs font-bold border border-sage/30">Me arsye</span>
                                            : <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-terracotta/10 text-terracotta text-xs font-bold border border-terracotta/30">Pa arsye</span>;
                                        return (
                                            <tr key={a.id} data-testid={`absence-row-${a.id}`} className="hover:bg-bone/30">
                                                <td className="px-4 sm:px-6 py-3 font-mono text-sm text-slate-700">{a.date}</td>
                                                <td className="px-4 sm:px-6 py-3 font-semibold text-slate-900 text-sm">{a.subject_name}</td>
                                                <td className="px-2 sm:px-4 py-3"><span className="inline-flex items-center px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-bold">{a.hours}h</span></td>
                                                <td className="px-2 sm:px-4 py-3">{reasonChip}</td>
                                                <td className="px-4 sm:px-6 py-3 text-sm text-slate-600 hidden sm:table-cell">{a.comment || "—"}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </Layout>
    );
}
